import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Spinner, Nav, Tab, Alert } from 'react-bootstrap';
import { FaBuilding, FaUser, FaPhone, FaEnvelope, FaCalendarAlt, FaUsers, FaMapMarkerAlt, 
  FaClipboardCheck, FaFileAlt, FaKey, FaToggleOn, FaToggleOff, FaPlus, FaArrowLeft, FaSync } from 'react-icons/fa';
import { Link, useParams } from 'react-router-dom';
import superadminAPI from '../../utils/superadminAPI';
import AddSiteModal from './AddSiteModal';

const CompanyAdminDetails = () => {
  // State variables
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState({});
  const [stats, setStats] = useState({
    personnelCount: 0,
    sitesCount: 0,
    checkInsCount: 0,
    reportsCount: 0
  });
  const [sites, setSites] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [error, setError] = useState(null);
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);
  
  // Get company admin ID from URL params
  const { id } = useParams();

  // Fetch company admin details when component mounts or ID changes
  useEffect(() => {
    const fetchCompanyAdminDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log(`Fetching details for company admin ID: ${id}`);
        const response = await superadminAPI.getCompanyAdminDetails(id);
        console.log('API Response:', response);
        
        if (!response || !response.data) {
          throw new Error('Empty response received from server');
        }
        
        const data = response.data;
        console.log('Response data:', data);
        
        // Validate and set admin data
        if (!data.admin || typeof data.admin !== 'object') {
          throw new Error('Invalid admin data in response');
        }
        setAdmin(data.admin);
        console.log('Admin data set:', data.admin);
        
        // Set stats with defaults if missing
        const statsData = data.stats || {
          personnelCount: 0,
          sitesCount: 0,
          checkInsCount: 0,
          reportsCount: 0
        };
        setStats(statsData);
        console.log('Stats data set:', statsData);
        
        // Set sites and personnel arrays
        const sitesData = Array.isArray(data.sites) ? data.sites : [];
        setSites(sitesData);
        console.log('Sites data set:', sitesData);
        
        const personnelData = Array.isArray(data.personnel) ? data.personnel : [];
        setPersonnel(personnelData);
        console.log('Personnel data set:', personnelData);
      } catch (error) {
        console.error('Error fetching company admin details:', error);
        setError(error.response?.data?.message || error.message || 'Failed to load company admin details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCompanyAdminDetails();
  }, [id]);

  const handleStatusToggle = async () => {
    try {
      const newStatus = admin.status === 'active' ? 'inactive' : 'active';
      await superadminAPI.updateCompanyAdminStatus(id, newStatus);
      
      // Update the local state
      setAdmin({
        ...admin,
        status: newStatus
      });

      // Show success message
      alert(`Company account ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
    } catch (error) {
      console.error('Error updating company admin status:', error);
      // Show error message with details if available
      alert(`Error: ${error.response?.data?.message || 'Failed to update company status. Please try again.'}`);
    }
  };

  const handleExportCredentials = async () => {
    try {
      const response = await superadminAPI.exportCompanyAdminCredentials(id);
      
      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `credentials_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting credentials:', error);
      alert(`Error: ${error.response?.data?.message || 'Failed to export credentials. Please try again.'}`);
    }
  };

  const handleSiteAdded = () => {
    // Refresh the data by reloading the page
    window.location.reload();
    setShowAddSiteModal(false);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading company admin details...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Card className="shadow-sm border-danger">
          <Card.Body className="text-center">
            <Alert variant="danger">
              <h4>Error Loading Data</h4>
              <p>{error}</p>
            </Alert>
            <div className="d-flex justify-content-center gap-3 flex-column flex-md-row">
              <Button 
                variant="primary" 
                as={Link} 
                to="/superadmin/company-admins"
                className="mt-3"
              >
                <FaArrowLeft className="me-2" /> Back to Company Admins
              </Button>
              <Button 
                variant="outline-primary" 
                className="mt-3"
                onClick={() => window.location.reload()}
              >
                <FaSync className="me-2" /> Try Again
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }
  
  // If admin data is not loaded and we're not in a loading state, show not found message
  if (!loading && (!admin || !admin.id)) {
    console.log('Admin data not found or invalid');
    return (
      <Container className="py-4">
        <Card className="shadow-sm">
          <Card.Body className="text-center">
            <h4>Company admin not found</h4>
            <Button 
              variant="primary" 
              as={Link} 
              to="/superadmin/company-admins"
              className="mt-3"
            >
              <FaArrowLeft className="me-2" /> Back to Company Admins
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }
  
  // Log the current state for debugging
  console.log('Current state before rendering:', { admin, stats, sites, personnel });

  return (
    <Container fluid className="py-3 py-md-4">
      <Row className="mb-3 mb-md-4">
        <Col xs={12} md={8}>
          <Button 
            variant="outline-primary" 
            as={Link} 
            to="/superadmin/company-admins"
            className="mb-3"
          >
            <FaArrowLeft className="me-2" /> Back to Company Admins
          </Button>
          <h1 className="mb-2">{admin.company}</h1>
          <p className="text-muted mb-0">Company Admin Details</p>
        </Col>
        <Col xs={12} md={4} className="d-flex align-items-center gap-2 flex-column flex-md-row mt-3 mt-md-0">
          <Button 
            variant="outline-warning" 
            className="d-flex align-items-center w-100 w-md-auto"
            onClick={handleExportCredentials}
          >
            <FaKey className="me-2" /> Generate Credentials
          </Button>
          <Button 
            variant={admin.status === 'active' ? 'outline-danger' : 'outline-success'} 
            className="d-flex align-items-center w-100 w-md-auto"
            onClick={handleStatusToggle}
          >
            {admin.status === 'active' ? <FaToggleOff className="me-2" /> : <FaToggleOn className="me-2" />}
            {admin.status === 'active' ? 'Deactivate' : 'Activate'}
          </Button>
        </Col>
      </Row>

      {/* Admin Info Card */}
      <Row className="mb-3 mb-md-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              <Row>
                <Col xs={12} md={6}>
                  <div className="d-flex mb-3">
                    <div className="rounded-circle bg-primary bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                      <FaBuilding className="text-primary" size={20} />
                    </div>
                    <div>
                      <h6 className="text-muted mb-1 small">Company</h6>
                      <h4 className="h5">{admin.company}</h4>
                    </div>
                  </div>
                  <div className="d-flex mb-3">
                    <div className="rounded-circle bg-info bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                      <FaUser className="text-info" size={20} />
                    </div>
                    <div>
                      <h6 className="text-muted mb-1 small">Admin Name</h6>
                      <h4 className="h5">{admin.name}</h4>
                    </div>
                  </div>
                  <div className="d-flex mb-3">
                    <div className="rounded-circle bg-success bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                      <FaEnvelope className="text-success" size={20} />
                    </div>
                    <div>
                      <h6 className="text-muted mb-1 small">Email</h6>
                      <h4 className="h5">{admin.email}</h4>
                    </div>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="d-flex mb-3">
                    <div className="rounded-circle bg-warning bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                      <FaPhone className="text-warning" size={20} />
                    </div>
                    <div>
                      <h6 className="text-muted mb-1 small">Phone</h6>
                      <h4 className="h5">{admin.phone || 'N/A'}</h4>
                    </div>
                  </div>
                  <div className="d-flex mb-3">
                    <div className="rounded-circle bg-danger bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                      <FaCalendarAlt className="text-danger" size={20} />
                    </div>
                    <div>
                      <h6 className="text-muted mb-1 small">Created On</h6>
                      <h4 className="h5">{formatDate(admin.createdAt)}</h4>
                    </div>
                  </div>
                  <div className="d-flex mb-3">
                    <div className="rounded-circle bg-dark bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                      <FaToggleOn className="text-dark" size={20} />
                    </div>
                    <div>
                      <h6 className="text-muted mb-1 small">Status</h6>
                      <h4 className="h5">
                        <Badge bg={admin && admin.status === 'active' ? 'success' : 'danger'}>
                          {admin && admin.status === 'active' ? 'Active' : 'Inactive'}
                        </Badge>
                      </h4>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-3 mb-md-4">
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-success bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                <FaUsers className="text-success" size={20} />
              </div>
              <div>
                <h6 className="text-muted mb-1 small">Personnel</h6>
                <h3 className="mb-0 h4">{stats.personnelCount}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-warning bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                <FaMapMarkerAlt className="text-warning" size={20} />
              </div>
              <div>
                <h6 className="text-muted mb-1 small">Sites</h6>
                <h3 className="mb-0 h4">{stats.sitesCount}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-info bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                <FaClipboardCheck className="text-info" size={20} />
              </div>
              <div>
                <h6 className="text-muted mb-1 small">Check-ins</h6>
                <h3 className="mb-0 h4">{stats.checkInsCount}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="h-100 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle bg-danger bg-opacity-10 p-2 p-md-3 me-2 me-md-3">
                <FaFileAlt className="text-danger" size={20} />
              </div>
              <div>
                <h6 className="text-muted mb-1 small">Reports</h6>
                <h3 className="mb-0 h4">{stats.reportsCount}</h3>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tabs for Sites and Personnel */}
      <Tab.Container defaultActiveKey="sites">
        <Row className="mb-3 mb-md-4">
          <Col>
            <Card className="shadow-sm">
              <Card.Header className="bg-white">
                <Nav variant="tabs" className="flex-column flex-md-row">
                  <Nav.Item>
                    <Nav.Link eventKey="sites">Sites</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="personnel">Personnel</Nav.Link>
                  </Nav.Item>
                </Nav>
              </Card.Header>
              <Card.Body>
                <Tab.Content>
                  <Tab.Pane eventKey="sites">
                    <div className="d-flex justify-content-between mb-3 flex-column flex-md-row">
                      <h5>Company Sites</h5>
                      <Button 
                        variant="primary" 
                        size="sm"
                        onClick={() => setShowAddSiteModal(true)}
                        className="mt-2 mt-md-0 w-100 w-md-auto"
                      >
                        <FaPlus className="me-2" /> Add Site
                      </Button>
                    </div>
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th className="d-none d-md-table-cell">Address</th>
                            <th className="d-none d-lg-table-cell">City</th>
                            <th className="d-none d-xl-table-cell">State</th>
                            <th className="d-none d-xl-table-cell">Zip Code</th>
                            <th className="d-none d-md-table-cell">Status</th>
                            <th className="d-none d-lg-table-cell">Created On</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sites.length > 0 ? (
                            sites.map((site) => (
                              <tr key={site.id}>
                                <td>
                                  <div>
                                    <div className="fw-bold">{site.name}</div>
                                    <small className="text-muted d-md-none">{site.address}</small>
                                  </div>
                                </td>
                                <td className="d-none d-md-table-cell">{site.address}</td>
                                <td className="d-none d-lg-table-cell">{site.city}</td>
                                <td className="d-none d-xl-table-cell">{site.state || 'N/A'}</td>
                                <td className="d-none d-xl-table-cell">{site.zipCode || 'N/A'}</td>
                                <td className="d-none d-md-table-cell">
                                  <Badge bg={site.status === 'active' ? 'success' : 'danger'}>
                                    {site.status === 'active' ? 'Active' : 'Inactive'}
                                  </Badge>
                                </td>
                                <td className="d-none d-lg-table-cell">{formatDate(site.createdAt)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center">No sites found</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Tab.Pane>
                  <Tab.Pane eventKey="personnel">
                    <h5 className="mb-3">Company Personnel</h5>
                    <div className="table-responsive">
                      <Table hover className="mb-0">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th className="d-none d-lg-table-cell">Email</th>
                            <th className="d-none d-xl-table-cell">Phone</th>
                            <th className="d-none d-md-table-cell">Position</th>
                            <th className="d-none d-md-table-cell">Status</th>
                            <th className="d-none d-lg-table-cell">Created On</th>
                          </tr>
                        </thead>
                        <tbody>
                          {personnel.length > 0 ? (
                            personnel.map((person) => (
                              <tr key={person.id}>
                                <td>
                                  <div>
                                    <div className="fw-bold">{person.name}</div>
                                    <small className="text-muted d-lg-none">{person.email}</small>
                                  </div>
                                </td>
                                <td className="d-none d-lg-table-cell">{person.email}</td>
                                <td className="d-none d-xl-table-cell">{person.phone || 'N/A'}</td>
                                <td className="d-none d-md-table-cell">{person.position || 'N/A'}</td>
                                <td className="d-none d-md-table-cell">
                                  <Badge bg={person.status === 'active' ? 'success' : 'danger'}>
                                    {person.status === 'active' ? 'Active' : 'Inactive'}
                                  </Badge>
                                </td>
                                <td className="d-none d-lg-table-cell">{formatDate(person.createdAt)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center">No personnel found</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Tab.Pane>
                </Tab.Content>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Tab.Container>

      {/* Add Site Modal */}
      <AddSiteModal 
        show={showAddSiteModal} 
        onHide={() => setShowAddSiteModal(false)} 
        companyId={id}
        companyName={admin.company}
        onSiteAdded={handleSiteAdded}
      />
    </Container>
  );
};

export default CompanyAdminDetails;
